
import { useEffect, useState, useCallback } from 'react';
import { supabase, clearAuthStorage } from '@/lib/supabase';
import { fetchUserProfile, isPublicRoute } from '@/utils/authUtils';
import type { User, Session } from '@supabase/supabase-js';
import type { UserProfile } from '@/types/auth';
import { NavigateFunction, Location } from 'react-router-dom';
import { toast } from 'sonner';

interface UseAuthInitializationProps {
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setSession: React.Dispatch<React.SetStateAction<Session | null>>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  navigate: NavigateFunction;
  location: Location;
  refreshSession: () => Promise<void>;
}

export const useAuthInitialization = ({
  setUser,
  setSession,
  setUserProfile,
  setLoading,
  navigate,
  location,
  refreshSession
}: UseAuthInitializationProps) => {
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  // Fonction pour gérer une redirection en cas d'erreur d'initialisation
  const handleAuthFailure = useCallback((message: string) => {
    console.error(`Auth initialization error: ${message}`);
    setInitializationError(message);
    setLoading(false);
    
    // Nettoyage du stockage local en cas d'erreur critique
    clearAuthStorage();
    
    // Rediriger vers la page de connexion si pas sur une route publique
    if (!isPublicRoute(location.pathname)) {
      navigate('/login', { 
        state: { 
          returnTo: location.pathname, 
          message: "Veuillez vous reconnecter."
        },
        replace: true
      });
    }
  }, [navigate, location.pathname, setLoading]);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let isAborted = false;
    let timeoutId: NodeJS.Timeout | null = null;
    
    const initAuth = async () => {
      if (isAborted || initialized) return;
      
      try {
        console.log("Démarrage de l'initialisation de l'authentification");
        setLoading(true);
        
        // Définir un timeout court pour le debug en cas de problème
        timeoutId = setTimeout(() => {
          if (!initialized && !isAborted) {
            console.warn("L'initialisation de l'auth a pris trop de temps (plus de 8 secondes)");
            toast.warning("La vérification de session prend plus de temps que prévu", {
              description: "Veuillez patienter quelques instants..."
            });
          }
        }, 8000);
        
        // Vérifier d'abord s'il existe déjà une session
        const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Erreur lors de la récupération de la session:", sessionError);
          handleAuthFailure(sessionError.message);
          return;
        }
        
        // Configurer ensuite le listener de changement d'état
        const { data } = await supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            console.log(`Événement d'auth détecté: ${event}`, currentSession?.user?.id);
            
            if (isAborted) return;
            
            if (currentSession) {
              setUser(currentSession.user);
              setSession(currentSession);
              
              // Récupérer le profil utilisateur
              const profile = await fetchUserProfile(currentSession.user.id);
              setUserProfile(profile);
              
              if (isPublicRoute(location.pathname) && location.pathname !== '/') {
                navigate('/index', { replace: true });
              }
            } else if (event === 'SIGNED_OUT') {
              console.log("Événement de déconnexion détecté");
              setUser(null);
              setSession(null);
              setUserProfile(null);
              
              if (!isPublicRoute(location.pathname)) {
                navigate('/login', { 
                  state: { returnTo: location.pathname },
                  replace: true
                });
              }
            }
            
            // Marquer l'initialisation comme terminée après avoir traité l'état
            if (!initialized) {
              setInitialized(true);
              setLoading(false);
              if (timeoutId) clearTimeout(timeoutId);
            }
          }
        );
        
        subscription = data.subscription;
        
        // Traiter la session existante maintenant que le listener est en place
        if (existingSession) {
          console.log("Session existante trouvée:", existingSession.user.id);
          setSession(existingSession);
          setUser(existingSession.user);
          
          // Récupérer le profil utilisateur
          const profile = await fetchUserProfile(existingSession.user.id);
          setUserProfile(profile);
          
          // Si l'utilisateur est sur une page publique (login, register), rediriger vers l'accueil
          if (isPublicRoute(location.pathname) && location.pathname !== '/') {
            console.log("Redirection automatique vers /index depuis", location.pathname);
            navigate('/index', { replace: true });
          }
        } else {
          console.log("Aucune session existante trouvée");
          // Si pas de session et route non publique, rediriger vers login
          if (!isPublicRoute(location.pathname)) {
            console.log("Redirection vers login car route protégée et pas de session");
            navigate('/login', { 
              state: { returnTo: location.pathname },
              replace: true
            });
          }
        }
        
        // Marquer l'initialisation comme terminée
        setInitialized(true);
        setLoading(false);
        if (timeoutId) clearTimeout(timeoutId);
        
      } catch (error: any) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error);
        handleAuthFailure(error.message || "Erreur d'initialisation");
      }
    };

    initAuth();

    // Nettoyage
    return () => {
      isAborted = true;
      if (subscription) subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    navigate, 
    location.pathname, 
    setUser, 
    setSession, 
    setLoading, 
    setUserProfile,
    refreshSession,
    handleAuthFailure,
    initialized
  ]);

  return { initializationError, initialized };
};
